import { bungie, BungieError } from '../lib/bungie';
import { idbGet, idbSet } from '../lib/idb';
import { items, table, tablesOf } from '../lib/manifest';
import { bungieName, type Player } from './hub';

/** Port of the app's ActivityLog, ActivityHeat, ActivitySeries, LegendStats, Relationships and BungieFriends. */

const val = (v: any, k: string): number => v?.[k]?.basic?.value ?? 0;

// ---------------- the activity history ----------------

export interface ActivityRow { when: string; kind: string; name: string; completed: boolean; instanceId: string; seconds: number; kills: number; deaths: number }
export const PAGE_SIZE = 25;
export const MODES: [number, string][] = [
  [0, 'All Activities'], [4, 'Raids'], [82, 'Dungeons'], [5, 'Crucible'], [69, 'Competitive'], [84, 'Trials of Osiris'], [19, 'Iron Banner'],
  [18, 'Strikes and Ops'], [46, 'Nightfalls'], [63, 'Gambit'], [2, 'Story'], [6, 'Patrol'], [87, 'Lost Sectors'],
];

const history = (p: Player, charId: string, mode: number, count: number, page: number) =>
  bungie<any>(`/Destiny2/${p.membershipType}/Account/${p.membershipId}/Character/${charId}/Stats/Activities/?mode=${mode}&count=${count}&page=${page}`);

export async function loadActivities(p: Player, charId: string, mode: number, page: number): Promise<ActivityRow[]> {
  const r = await history(p, charId, mode, PAGE_SIZE, page);
  const acts: any[] = r.activities ?? [];
  const t = await tablesOf('DestinyActivityDefinition', 'DestinyActivityTypeDefinition');
  return acts.map((a) => {
    const ref = a.activityDetails?.referenceId, dir = a.activityDetails?.directorActivityHash;
    const def = t.DestinyActivityDefinition[ref];
    const playlist = dir !== ref ? t.DestinyActivityDefinition[dir]?.displayProperties?.name : '';
    // a playlist (Crucible, Quickplay) is named by its playlist with the map beside it; anything else by its kind
    const kind = playlist || t.DestinyActivityTypeDefinition[def?.activityTypeHash]?.displayProperties?.name || 'Activity';
    const v = a.values;
    return {
      when: a.period, kind, name: def?.displayProperties?.name ?? '', completed: val(v, 'completed') > 0 && val(v, 'completionReason') !== 2,
      instanceId: a.activityDetails?.instanceId ?? '', seconds: val(v, 'activityDurationSeconds'), kills: val(v, 'kills'), deaths: val(v, 'deaths'),
    };
  });
}

/** The page on which the activities of that day begin (the history read back 250 at a time, up to 5,000). */
export async function pageForDate(p: Player, charId: string, mode: number, day: Date): Promise<number> {
  const end = new Date(day.getFullYear(), day.getMonth(), day.getDate() + 1);
  let index = 0;
  for (let page = 0; page < 20; page++) {
    const acts: any[] = (await history(p, charId, mode, 250, page)).activities ?? [];
    for (const a of acts) {
      if (new Date(a.period) < end) return Math.floor(index / PAGE_SIZE);
      index++;
    }
    if (acts.length < 250) break;
  }
  return Math.max(0, Math.floor((index - 1) / PAGE_SIZE));
}

// bookmarks: activities starred to find again, kept in this browser
const MARKS = 'd2c.bookmarks';
export const bookmarks = (): ActivityRow[] => { try { return JSON.parse(localStorage.getItem(MARKS) ?? '[]'); } catch { return []; } };
export const isBookmarked = (id: string) => !!id && bookmarks().some((b) => b.instanceId === id);
export function toggleBookmark(r: ActivityRow) {
  if (!r.instanceId) return;
  const all = bookmarks();
  const next = all.some((b) => b.instanceId === r.instanceId) ? all.filter((b) => b.instanceId !== r.instanceId) : [...all, r].sort((a, b) => b.when.localeCompare(a.when));
  try { localStorage.setItem(MARKS, JSON.stringify(next)); } catch { /* storage blocked */ }
}

// ---------------- Heat: a year of days ----------------

export type Days = Record<string, { seconds: number; count: number }>;
export const dayKey = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

async function addDays(days: Days, type: number, mid: string, charId: string) {
  const oldest = Date.now() - 372 * 86_400_000;
  for (let page = 0; page < 12; page++) {
    const acts: any[] = (await bungie<any>(`/Destiny2/${type}/Account/${mid}/Character/${charId}/Stats/Activities/?mode=0&count=250&page=${page}`)).activities ?? [];
    let done = acts.length < 250;
    for (const a of acts) {
      const when = new Date(a.period);
      if (when.getTime() < oldest) { done = true; continue; }
      const k = dayKey(when);
      const d = days[k] ?? { seconds: 0, count: 0 };
      // a stuck session can report days; no single activity counts for more than 6 hours
      days[k] = { seconds: Math.min(86400, d.seconds + Math.min(val(a.values, 'timePlayedSeconds'), 6 * 3600)), count: d.count + 1 };
    }
    if (done) break;
  }
}

export async function loadHeat(p: Player, charId: string): Promise<Days> {
  const days: Days = {};
  await addDays(days, p.membershipType, p.membershipId, charId);
  return days;
}

/** The platform account a membership id belongs to (the one played last, when cross save joins several). */
async function resolveMember(membershipId: string): Promise<{ type: number; mid: string }> {
  const linked = await bungie<any>(`/Destiny2/-1/Profile/${membershipId}/LinkedProfiles/?getAllMemberships=true`);
  const profile = [...(linked.profiles ?? [])].sort((a, b) => Number(b.membershipId === membershipId) - Number(a.membershipId === membershipId) || (b.dateLastPlayed ?? '').localeCompare(a.dateLastPlayed ?? ''))[0];
  if (!profile) throw new BungieError(0, 'NoAccount', 'Bungie has no Destiny 2 account for this player.');
  return { type: profile.membershipType, mid: profile.membershipId };
}

/** A player's whole year, all characters together. Public, unless they hide it. */
export async function loadPlayerHeat(membershipId: string): Promise<Days> {
  const { type, mid } = await resolveMember(membershipId);
  const chars = (await bungie<any>(`/Destiny2/${type}/Profile/${mid}/?components=200`)).characters?.data;
  if (!chars) throw new BungieError(0, 'Private', 'This player keeps their characters private.');
  const days: Days = {};
  for (const cid of Object.keys(chars)) await addDays(days, type, mid, cid);
  return days;
}

export async function seasonSpans(): Promise<{ name: string; start: Date; end: Date }[]> {
  const t = await table<any>('DestinySeasonDefinition');
  return Object.values<any>(t).filter((s) => s.displayProperties?.name && s.startDate && s.endDate)
    .map((s) => ({ name: s.displayProperties.name, start: new Date(s.startDate), end: new Date(s.endDate) }));
}

// ---------------- Series: clears and best times per raid, dungeon... ----------------

export const SERIES_KINDS = ['Raid', 'Dungeon', 'Exotic Mission', 'Lost Sector', 'Nightfall', 'Strike', 'Seasonal Arena'];
export interface SeriesEntry { name: string; image: string; clears: number; best: number; kills: number; deaths: number; seconds: number; hashes: number[] }
const VARIANT = /:\s*(Customize|Custom|Matchmade|Standard|Private|Normal|Advanced|Expert|Master|Legend|Legendary|Ultimate|Grandmaster|Brave|Fabled|Prestige|Guided Games)\b.*$/i;
// one tile per activity: the difficulty is dropped, and the Pantheon's weekly line-ups count as one
const tidy = (n: string) => (/^(The )?Pantheon:/i.test(n) ? 'The Pantheon' : n.replace(VARIANT, '').trim());

export async function loadSeries(p: Player, charId: string): Promise<Record<string, SeriesEntry[]>> {
  const r = await bungie<any>(`/Destiny2/${p.membershipType}/Account/${p.membershipId}/Character/${charId}/Stats/AggregateActivityStats/`);
  const t = await tablesOf('DestinyActivityDefinition', 'DestinyActivityTypeDefinition');
  const result: Record<string, SeriesEntry[]> = Object.fromEntries(SERIES_KINDS.map((k) => [k, []]));
  const groups = new Map<string, { kind: string; name: string; rows: { hash: number; v: any }[] }>();
  for (const a of r.activities ?? []) {
    const def = t.DestinyActivityDefinition[a.activityHash];
    if (!def) continue;
    const kind = t.DestinyActivityTypeDefinition[def.activityTypeHash]?.displayProperties?.name ?? '';
    const name = tidy(def.displayProperties?.name ?? '');
    const key = `${kind}|${name}`;
    const g = groups.get(key) ?? { kind, name, rows: [] as { hash: number; v: any }[] };
    g.rows.push({ hash: a.activityHash, v: a.values });
    groups.set(key, g);
  }
  for (const g of groups.values()) {
    if (!g.name || !result[g.kind]) continue;
    const clears = g.rows.reduce((s, x) => s + val(x.v, 'activityCompletions'), 0);
    const seconds = g.rows.reduce((s, x) => s + val(x.v, 'activitySecondsPlayed'), 0);
    if (clears === 0 && seconds < 60) continue;
    const times = g.rows.map((x) => val(x.v, 'fastestCompletionMsForActivity') / 1000).filter((s) => s > 0);
    result[g.kind].push({
      name: g.name, image: g.rows.map((x) => t.DestinyActivityDefinition[x.hash]?.pgcrImage).find(Boolean) ?? '', clears, best: times.length ? Math.min(...times) : 0,
      kills: g.rows.reduce((s, x) => s + val(x.v, 'activityKills'), 0), deaths: g.rows.reduce((s, x) => s + val(x.v, 'activityDeaths'), 0), seconds,
      hashes: [...new Set(g.rows.map((x) => x.hash))],
    });
  }
  for (const list of Object.values(result)) list.sort((a, b) => b.clears - a.clears || a.name.localeCompare(b.name));
  return result;
}

/** Every run of one activity by a character, newest first (up to 1,000 activities of that kind back). */
export async function loadRuns(p: Player, charId: string, kind: string, hashes: number[]): Promise<ActivityRow[]> {
  const mode = ({ Raid: 4, Dungeon: 82, 'Lost Sector': 87, Nightfall: 46, Strike: 18 } as Record<string, number>)[kind] ?? 0;
  const want = new Set(hashes);
  const runs: ActivityRow[] = [];
  for (let page = 0; page < 4; page++) {
    const acts: any[] = (await history(p, charId, mode, 250, page)).activities ?? [];
    for (const a of acts) {
      const d = a.activityDetails;
      if (!want.has(d?.referenceId) && !want.has(d?.directorActivityHash)) continue;
      const v = a.values;
      runs.push({ when: a.period, kind, name: '', completed: val(v, 'completed') > 0 && val(v, 'completionReason') !== 2, instanceId: d?.instanceId ?? '', seconds: val(v, 'activityDurationSeconds'), kills: val(v, 'kills'), deaths: val(v, 'deaths') });
    }
    if (acts.length < 250) break;
  }
  return runs;
}

// ---------------- Legend: lifetime numbers ----------------

export type Stat = { value: number; text: string };
export interface LegendStats { pve: Record<string, Stat>; pvp: Record<string, Stat>; priv: Record<string, Stat> }

const readStats = (from: any): Record<string, Stat> =>
  Object.fromEntries(Object.entries<any>(from ?? {}).map(([k, v]) => [k, { value: v?.basic?.value ?? 0, text: v?.basic?.displayValue ?? '' }]));

export async function loadLegend(p: Player): Promise<LegendStats> {
  const s = await bungie<any>(`/Destiny2/${p.membershipType}/Account/${p.membershipId}/Stats/?groups=General,Weapons,Medals`);
  const results = s.mergedAllCharacters?.results;
  let priv = {};
  // custom games: Bungie counts private matches apart from the rest (one more small request; empty if it fails)
  try { priv = readStats((await bungie<any>(`/Destiny2/${p.membershipType}/Account/${p.membershipId}/Character/0/Stats/?modes=32&groups=General&periodType=2`)).privateMatches?.allTime); } catch { /* left empty */ }
  return { pve: readStats(results?.allPvE?.allTime), pvp: readStats(results?.allPvP?.allTime), priv };
}

export interface WeaponKills { item: number; name: string; icon: string; kills: number; precision: number }

export async function loadWeaponKills(p: Player, charIds: string[]): Promise<WeaponKills[]> {
  const totals = new Map<number, { kills: number; precision: number }>();
  for (const cid of charIds) {
    const r = await bungie<any>(`/Destiny2/${p.membershipType}/Account/${p.membershipId}/Character/${cid}/Stats/UniqueWeapons/`);
    for (const w of r.weapons ?? []) {
      const t = totals.get(w.referenceId) ?? { kills: 0, precision: 0 };
      totals.set(w.referenceId, { kills: t.kills + val(w.values, 'uniqueWeaponKills'), precision: t.precision + val(w.values, 'uniqueWeaponPrecisionKills') });
    }
  }
  const lite = await items();
  return [...totals.entries()].filter(([h, t]) => t.kills > 0 && lite[h]?.name)
    .map(([h, t]) => ({ item: h, name: lite[h].name, icon: lite[h].icon, kills: t.kills, precision: t.precision }))
    .sort((a, b) => b.kills - a.kills);
}

export interface PlayTime { total: number; pve: number; pvp: number; priv: number }

/** Another player's hours (public data). */
export async function loadPlayTime(membershipId: string): Promise<PlayTime> {
  const { type, mid } = await resolveMember(membershipId);
  const chars = (await bungie<any>(`/Destiny2/${type}/Profile/${mid}/?components=200`)).characters?.data;
  if (!chars) throw new BungieError(0, 'Private', 'This player keeps their characters private.');
  const total = Object.values<any>(chars).reduce((s, c) => s + Number(c.minutesPlayedTotal ?? 0), 0) * 60;
  const results = (await bungie<any>(`/Destiny2/${type}/Account/${mid}/Stats/?groups=General`)).mergedAllCharacters?.results;
  let priv = 0;
  try { priv = val((await bungie<any>(`/Destiny2/${type}/Account/${mid}/Character/0/Stats/?modes=32&groups=General&periodType=2`)).privateMatches?.allTime, 'secondsPlayed'); } catch { /* none */ }
  return { total, pve: val(results?.allPvE?.allTime, 'secondsPlayed'), pvp: val(results?.allPvP?.allTime, 'secondsPlayed'), priv };
}

// ---------------- who you play with ----------------

export interface Companion { memberId: string; name: string; activities: number; fireteams: number; duos: number }
export interface RelationshipData { people: Companion[]; reports: number; days: number }
export const REL_DAYS = 90, REL_LONG_DAYS = 365;

/** A match report: downloaded once, then kept in this browser (they never change). */
export async function pgcr(id: string): Promise<any> {
  const key = `pgcr:${id}`;
  const kept = await idbGet<any>(key);
  if (kept) return kept;
  const r = await bungie<any>(`/Destiny2/Stats/PostGameCarnageReport/${id}/`, { stats: true });
  // only what the pages read is kept: who was there, their fireteam, their numbers
  const slim = {
    period: r.period, activityDetails: r.activityDetails,
    entries: (r.entries ?? []).map((e: any) => ({ player: { destinyUserInfo: e.player?.destinyUserInfo, characterClass: e.player?.characterClass }, characterId: e.characterId, values: e.values })),
  };
  void idbSet(key, slim);
  return slim;
}

export async function loadRelationships(p: Player, days: number, onProgress?: (done: number, total: number) => void): Promise<RelationshipData> {
  const chars = (await bungie<any>(`/Destiny2/${p.membershipType}/Profile/${p.membershipId}/?components=200`)).characters?.data ?? {};
  const since = Date.now() - days * 86_400_000;
  const reports = new Set<string>();
  for (const cid of Object.keys(chars))
    for (let page = 0; page < 12; page++) {   // 250 a page, newest first: stops at the first page that reaches back far enough
      const acts: any[] = (await history(p, cid, 0, 250, page)).activities ?? [];
      let old = false;
      for (const a of acts) {
        if (new Date(a.period).getTime() < since) { old = true; continue; }
        if (a.activityDetails?.instanceId) reports.add(a.activityDetails.instanceId);
      }
      if (old || acts.length < 250) break;
    }
  const seen = new Map<string, { name: string; activities: number; fireteams: number; duos: number }>();
  const ids = [...reports];
  let done = 0, next = 0;
  const worker = async () => {
    while (next < ids.length) {
      const id = ids[next++];
      try {
        const r = await pgcr(id);
        const entries: { id: string; name: string; team: string }[] = [];
        for (const e of r.entries ?? []) {
          const u = e.player?.destinyUserInfo;
          if (!u?.membershipId || entries.some((x) => x.id === u.membershipId)) continue;
          entries.push({ id: u.membershipId, name: bungieName(u), team: String(e.values?.fireteamId?.basic?.value ?? '') });
        }
        const me = entries.find((e) => e.id === p.membershipId);
        const mates = new Set(entries.filter((e) => e.id !== p.membershipId && me?.team && me.team !== '0' && e.team === me.team).map((e) => e.id));
        for (const e of entries) {
          if (e.id === p.membershipId) continue;
          const o = seen.get(e.id) ?? { name: e.name, activities: 0, fireteams: 0, duos: 0 };
          seen.set(e.id, { name: e.name || o.name, activities: o.activities + 1, fireteams: o.fireteams + (mates.has(e.id) ? 1 : 0), duos: o.duos + (mates.size === 1 && mates.has(e.id) ? 1 : 0) });
        }
      } catch { /* a report Bungie no longer has: skipped */ }
      onProgress?.(++done, ids.length);
    }
  };
  await Promise.all(Array.from({ length: 12 }, worker));
  return {
    people: [...seen.entries()].map(([memberId, v]) => ({ memberId, ...v })).sort((a, b) => b.activities - a.activities || a.name.localeCompare(b.name)),
    reports: ids.length, days,
  };
}

export interface BungieFriend { memberId: string; name: string; online: boolean }

export async function loadFriends(token: string): Promise<BungieFriend[]> {
  const r = await bungie<any>('/Social/Friends/', { token });
  const list: BungieFriend[] = [];
  for (const f of r.friends ?? []) {
    if ((f.lastSeenAsMembershipId ?? '').length < 2 || !f.bungieGlobalDisplayName) continue;   // never seen in Destiny: no year to show
    const name = f.bungieGlobalDisplayName + (f.bungieGlobalDisplayNameCode > 0 ? `#${String(f.bungieGlobalDisplayNameCode).padStart(4, '0')}` : '');
    list.push({ memberId: f.lastSeenAsMembershipId, name, online: f.onlineStatus === 1 });
  }
  return list.sort((a, b) => Number(b.online) - Number(a.online) || a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }));
}
